import {
  Box,
  Button,
  Flex,
  Heading,
  forwardRef,
  Text,
} from "@chakra-ui/react";
import { Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useEffect, useRef, useState } from "react";
import { Avatar } from "../../components/Avatar";
import { Filter } from "../../components/Filter";
import WithAuth from "../../hoc/withAuth";
import { useTransfer } from "../../hooks/useTransfer";
import { Balance } from "../../models/Transfer";
import { MinusCircle } from "../../styles/icons/MinusCircle";
import { PlusCircle } from "../../styles/icons/PlusCircle";
import { formatCurrency, formatDate, getOperationMethod, showToast } from "../../utils";
import DatePicker, { registerLocale } from "react-datepicker";
import { startOfMonth, lastDayOfMonth, format } from "date-fns";
import ptBR from "date-fns/locale/pt-BR"
import { CalendarIcon } from "../../styles/icons/CalendarIcon";
import { FlashLightIcon } from "../../styles/icons/FlashLightIcon";
import { Checkbox } from "../../components/Forms/Checkbox";
import { useForm } from "react-hook-form";

export function HistoryTransfer() {
  const [historicTransfers, setHistoricTransfers] = useState<Balance[]>([]);
  const [filteredHistoricTransfers, setFilteredHistoricTransfers] = useState<Balance[]>([]);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isOperationFilterOpen, setIsOperationFilterOpen] = useState(false);
  const { getTransferStatement } = useTransfer();
  const [startDate, setStartDate] = useState<Date>(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState<Date>(lastDayOfMonth(new Date()));
  const operationFilterRef = useRef();
  const [loading, setLoading] = useState<boolean>(false);
  registerLocale("ptBR", ptBR);
  const {
    register,
    setValue,
    watch,
  } = useForm();
  const transferChecked = watch("transferChecked");
  const antecipationChecked = watch("antecipationChecked");
  const debitCorrectionChecked = watch("debitCorrectionChecked");
  const creditCorrectionChecked = watch("creditCorrectionChecked");
  const discountChecked = watch("discountChecked");

  const historicColumns: ColumnsType<Balance> = [
    {
      key: "sellerName",
      title: "Seller",
      dataIndex: "sellerName",
      render: (sellerAccountancy, record) => (
        <Text>
          <Avatar name={record?.sellerAccountancy?.sellerName} />
        </Text>
      )
    },
    {
      key: "statementType",
      title: "Operação",
      dataIndex: "statementType",
      render: (sellerAccountancy, record) => (
        <Flex align="center" gap="2">
          {record?.creditOperation ? <PlusCircle color="#52C41A" size={15} /> : <MinusCircle color="#FF4D4F" size={15} />}
          <Text>{getOperationMethod(record?.statementType)}</Text>
        </Flex>
      )
    },
    {
      key: "executionDate",
      title: "Data",
      dataIndex: "executionDate",
      render: (conclude, record) => (
        <Text>
          {formatDate(record?.executionDate)}
        </Text>
      )
    },
    {
      key: "amount",
      title: "Valor",
      dataIndex: "amount",
      render: (amount, record) => (
        <Text
          fontWeight={700}
          color={record.creditOperation ? "gray.700" : "red.700"}
        >
          {formatCurrency({ value: amount })}
        </Text>
      )
    },
  ];

  async function handleGetOnSubmit(search?: string) {
    setLoading(true)
    try {
      const response = await getTransferStatement(
        {
          companyName: search ? search : "",
          executionDateInitial: format(startDate, "yyyy-MM-dd"),
          executionDateFinal: format(endDate, "yyyy-MM-dd"),
          types: `${transferChecked ? "DEBIT_TRANSFER," : ""}${antecipationChecked ? "INSTALLMENTS_ANTICIPATION," : ""}${debitCorrectionChecked ? "DEBIT_CORRECTION," : ""}${creditCorrectionChecked ? "CREDIT_CORRECTION," : ""}${discountChecked ? "FEE_DISCOUNT" : ""}`,
          sort: "executionDate,desc",
        },
        {
          size: 10000,
        }
      );

      const formattedHistoricData = response.data.content.map((data: Balance, index: number) => {
        return {
          ...data,
          key: index
        }
      })
      setHistoricTransfers(formattedHistoricData);
    } catch (error: any) {
      error?.response?.data?.errors?.map((error: any) => (
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`
        })
      ));
    } finally {
      setIsOperationFilterOpen(false);
      setLoading(false)
    }
  }

  async function handleGetInitialData() {
    setLoading(true)

    const types = [
      transferChecked ? "DEBIT_TRANSFER" : "",
      antecipationChecked ? "INSTALLMENTS_ANTICIPATION" : "",
      debitCorrectionChecked ? "DEBIT_CORRECTION" : "",
      creditCorrectionChecked ? "CREDIT_CORRECTION" : "",
      discountChecked ? "FEE_DISCOUNT" : ""
    ].filter(Boolean);


    try {
      const response = await getTransferStatement(
        {
          executionDateInitial: format(startDate, "yyyy-MM-dd"),
          executionDateFinal: format(endDate, "yyyy-MM-dd"),
          types,
          sort: "executionDate,desc",
        },
        {
          size: 10000,
        }
      );

      const formattedHistoricData = response.data.content.map((data: Balance, index: number) => {
        return {
          ...data,
          key: index
        }
      })
      setHistoricTransfers(formattedHistoricData);
      setFilteredHistoricTransfers(formattedHistoricData);
    } catch (error: any) {
      error?.response?.data?.errors?.map((error: any) => (
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`
        })
      ));
    } finally {
      setIsOperationFilterOpen(false);
      setLoading(false)
    }
  }

  useEffect(() => {
    handleGetInitialData();
    // eslint-disable-next-line
  }, [startDate, endDate]);

  function handleCleanFilterValues() {
    setValue("transferChecked", false);
    setValue("antecipationChecked", false);
    setValue("debitCorrectionChecked", false);
    setValue("creditCorrectionChecked", false);
    setValue("discountChecked", false);
  }
  const CustomInput = forwardRef(({ value }: any, ref) => (
    <Button
      onClick={() => setIsDatePickerOpen(true)}
      ref={ref}
      p="5px 12px"
      fontSize="14px"
      borderRadius="4"
      _hover={{}}
      bgColor="white"
      color="black"
      border="1px solid #D9D9D9"
      fontWeight="500"
    >
      <Flex
        align="center"
        gap="4"
      >
        <CalendarIcon size={22} />
        <Text>Data</Text>
      </Flex>
    </Button>
  ));
  const TypeOration = forwardRef(({ value }: any, ref) => (
    <Flex
      position="relative"
      ref={ref}
    >
      <Button
        onClick={() => setIsOperationFilterOpen(!isOperationFilterOpen)}
        p="5px 12px"
        fontSize="14px"
        borderRadius="4"
        _hover={{}}
        bgColor="white"
        color="black"
        border="1px solid #D9D9D9"
        fontWeight="500"
      >
        <Flex
          align="center"
          gap="4"
        >
          <FlashLightIcon size={22} />
          <Text>Operação</Text>
        </Flex>
      </Button>
      {isOperationFilterOpen && (
        <Flex
          ref={operationFilterRef}
          w="calc(100% + 50%)"
          top="calc(100% + 5px)"
          bgColor="white"
          position="absolute"
          border="1px solid #D9D9D9"
          zIndex="1"
          direction="column"
        >
          <Flex
            w="100%"
            p="2"
            direction="column"
          >
            <Checkbox
              name="transferChecked"
              label="Repasse"
              register={{ ...register("transferChecked") }}
            />
          </Flex>
          <Flex
            w="100%"
            borderTop="1px solid #D9D9D9"
            p="2"
            direction="column"
            gap="4"
          >
            <Text
              fontSize="12px"
            >
              Débito
            </Text>
            <Checkbox
              name="antecipationChecked"
              label="Antecipação de recebíveis"
              register={{ ...register("antecipationChecked") }}
            />
            <Checkbox
              name="debitCorrectionChecked"
              label="Correção"
              register={{ ...register("debitCorrectionChecked") }}
            />
          </Flex>
          <Flex
            w="100%"
            borderTop="1px solid #D9D9D9"
            p="2"
            direction="column"
            gap="4"
          >
            <Text
              fontSize="12px"
            >
              Crédito
            </Text>
            <Checkbox
              name="discountChecked"
              label="Desconto em antecipação"
              register={{ ...register("discountChecked") }}
            />
            <Checkbox
              name="creditCorrectionChecked"
              label="Correção"
              register={{ ...register("creditCorrectionChecked") }}
            />
          </Flex>
          <Flex
            w="100%"
            borderTop="1px solid #D9D9D9"
            p="2"
            gap="2"
            justifyContent="space-between"
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCleanFilterValues}
            >
              Limpar
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleGetInitialData}
            >
              Filtrar
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  ));

  const onDateChange = (dates: any) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    if (start && end) return setIsDatePickerOpen(false);
  };
  return (
    <Box 
      letterSpacing="tighter"
    >
      <Flex
        flexDir="column"
      >
        <Heading marginBottom="2rem">Histórico de movimentações</Heading>
        <Filter
          placeholder="Buscar seller"
          onSubmitSearch={handleGetOnSubmit}
          needToSubmit
          loading={loading}
        >
          <DatePicker
            locale="ptBR"
            selected={startDate}
            onChange={(date) => onDateChange(date)}
            startDate={startDate}
            endDate={endDate}
            dateFormat="MMMM/yyyy"
            onInputClick={() => setIsDatePickerOpen(true)}
            onClickOutside={() => setIsDatePickerOpen(false)}
            open={isDatePickerOpen}
            customInput={<CustomInput />}
            selectsRange
          />
          <TypeOration />
        </Filter>
        <Table<Balance>
          columns={historicColumns}
          dataSource={
            filteredHistoricTransfers?.length > 0
              ? filteredHistoricTransfers
              : historicTransfers
          }
          rowKey={(record) => record.key}
          locale={{ emptyText: "Sem repasses cadastrados" }}
        />
      </Flex>
    </Box>
  );
}

export default WithAuth(HistoryTransfer);